import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/utils/tw-helpers'
import * as Popper from '@radix-ui/react-popper'
import { Portal } from '@radix-ui/react-portal'
import { useSize } from '@radix-ui/react-use-size'
import { observeElementRect } from '@radix-ui/rect'
import { Fragment, useEffect, useRef, useState } from 'react'
import { RubyText } from './ruby-text/RubyText'

interface TutorialStepProps {
  children: React.ReactElement
  hightlightSelector?: string
  paddingX?: number
  paddingY?: number
  borderRadius?: number
  isOpen?: boolean
  onRequestClose?(): void

  messageSide?: 'top' | 'right' | 'bottom' | 'left'
  messageOffset?: number
  align?: 'start' | 'end' | 'center'
  alignOffset?: number
  message?: string | string[]

  className?: string
}

export function TutorialStep(props: TutorialStepProps): JSX.Element {
  const {
    children,
    hightlightSelector,
    paddingX = 0,
    paddingY = 0,
    borderRadius = 16,
    isOpen,
    onRequestClose,
    messageSide = 'bottom',
    messageOffset = 0,
    align = 'center',
    alignOffset = 0,
    message,
    className,
  } = props

  const bodySize = useSize(document.body)
  const rectsRef = useRef(new Map<Element, DOMRect>())
  const [, setCounter] = useState(0)

  const isMobile = useIsMobile()

  const referenceRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const elements = hightlightSelector
      ? document.querySelectorAll(hightlightSelector)
      : []
    const unobserves: Function[] = []

    function observe(e: Element) {
      const unobserve = observeElementRect(e, (rect: DOMRect) => {
        rectsRef.current.set(e, rect)
        // force rerender
        setCounter((c) => c + 1)
      })
      unobserves.push(unobserve)
    }

    elements.forEach(observe)

    // ensure that reference ref got highlighted
    if (referenceRef.current && !new Set(elements).has(referenceRef.current)) {
      observe(referenceRef.current)
    }

    return () => {
      unobserves.forEach((unobserve) => unobserve())
      rectsRef.current = new Map<Element, DOMRect>()
      setCounter(0)
    }
  }, [hightlightSelector, isOpen])

  const cutoutHoles: JSX.Element[] = []
  rectsRef.current.forEach((rect) => {
    cutoutHoles.push(
      <rect
        key={cutoutHoles.length}
        x={rect.left - paddingX}
        y={rect.top - paddingY}
        width={rect.width + 2 * paddingX}
        height={rect.height + 2 * paddingY}
        fill="black"
        rx={borderRadius}
        ry={borderRadius}
      />
    )
  })

  let pointerMaskPath = ''
  if (bodySize) {
    pointerMaskPath = `M 0,0 h ${bodySize.width} v ${bodySize.height} h -${bodySize.width} z `
    rectsRef.current.forEach(({ left, top, height, width }) => {
      const x = left - paddingX
      const y = top - paddingY
      const w = width + 2 * paddingX
      const h = height + 2 * paddingY
      pointerMaskPath += `M ${x},${y} v ${h} h ${w} v -${h} z `
    })
  }

  return isOpen ? (
    <Popper.Root>
      <Popper.Anchor ref={referenceRef} onClick={onRequestClose} asChild>
        {children}
      </Popper.Anchor>

      <Portal asChild>
        <>
          {bodySize && (
            <svg
              className="pointer-events-none fixed inset-0 z-50 opacity-50"
              style={bodySize}
            >
              <defs>
                <mask id="mask">
                  <rect x={0} y={0} {...bodySize} fill="white" />

                  {cutoutHoles}
                </mask>
              </defs>

              {
                <rect
                  x={0}
                  y={0}
                  {...bodySize}
                  mask="url(#mask)"
                  className={'fill-[#2c3039]'}
                />
              }

              {pointerMaskPath && (
                <path
                  className={'fill-[#2c3039]'}
                  d={pointerMaskPath}
                  style={{ pointerEvents: 'painted' }}
                />
              )}
            </svg>
          )}

          {message && (
            <Popper.Content
              align={align}
              alignOffset={alignOffset}
              side={messageSide}
              sideOffset={(isMobile ? 18 : 22) + messageOffset}
              className={cn(
                className,
                'z-50',
                '[&[data-side=top]_[data-tutorial-arrow-top]]:visible',
                '[&[data-side=bottom]_[data-tutorial-arrow-bottom]]:visible',
                '[&[data-side=left]_[data-tutorial-arrow-left]]:visible',
                '[&[data-side=right]_[data-tutorial-arrow-right]]:visible'
              )}
            >
              <div className="relative rounded-[16px] bg-basic-bg px-[20px] py-[16px] shadow-[0_4px_4px_0_#00000026] md:px-[24px] md:py-[18px]">
                <div
                  data-tutorial-arrow
                  data-tutorial-arrow-bottom
                  className={cn(
                    'invisible',
                    'absolute h-0 w-0 border-basic-bg',
                    'border-x-[10.5px] border-b-[12px] border-x-transparent md:border-x-[12px] md:border-b-[15px]',
                    '-top-[12px] left-[50%] -ml-[10.5px] md:-top-[15px] md:-ml-[12px]'
                  )}
                />
                <div
                  data-tutorial-arrow
                  data-tutorial-arrow-top
                  className={cn(
                    'invisible',
                    'absolute h-0 w-0 border-basic-bg',
                    'border-x-[10.5px] border-t-[12px] border-x-transparent md:border-x-[12px] md:border-t-[15px]',
                    '-bottom-[12px] left-[50%] -ml-[10.5px] md:-bottom-[15px] md:-ml-[12px]'
                  )}
                />
                <div
                  data-tutorial-arrow
                  data-tutorial-arrow-right
                  className={cn(
                    'invisible',
                    'absolute h-0 w-0 border-basic-bg',
                    'border-y-[10.5px] border-r-[12px] border-y-transparent md:border-y-[12px] md:border-r-[15px]',
                    '-left-[12px] top-[50%] -mt-[10.5px] md:-left-[15px] md:-mt-[12px]'
                  )}
                />
                <div
                  data-tutorial-arrow
                  data-tutorial-arrow-left
                  className={cn(
                    'invisible',
                    'absolute h-0 w-0 border-basic-bg',
                    'border-y-[10.5px] border-l-[12px] border-y-transparent md:border-y-[12px] md:border-l-[15px]',
                    '-right-[12px] top-[50%] -mt-[10.5px] md:-right-[15px] md:-mt-[12px]'
                  )}
                />
                <p className="relative z-10 text-[14px] font-medium leading-[1.7] tracking-wider text-secondary-20 md:text-[18px]">
                  {typeof message == 'string' ? (
                    <RubyText>{message}</RubyText>
                  ) : (
                    message.map((msg, index) => (
                      <Fragment key={`tutorial-msg-${index}`}>
                        <RubyText>{msg}</RubyText>
                        <br />
                      </Fragment>
                    ))
                  )}
                </p>
              </div>
            </Popper.Content>
          )}
        </>
      </Portal>
    </Popper.Root>
  ) : (
    children
  )
}
